import { useForm } from '@mantine/form';
import Radiobutton from '@/components/RadioButton';
import { useNavigate } from 'react-router-dom';
import { REVERSE_MORTGAGE_ACTIONS } from '@/data/constants';
import { useState } from 'react';
import { useSaveMutation, useReverseMortgageCalculateMaxLoanMutation } from '@/services/api';
import { useDispatch, useSelector } from 'react-redux';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import validate from '@/helpers/validations';
import { getMutationResult } from '@/helpers/query';
import { useTranslation } from 'react-i18next';
import { Page, ProcessHeader } from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import { toISOString } from '@/helpers/date';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import { notifications } from '@mantine/notifications';
import ProcessButton from '@/components/ProcessButton';
import ReasonIcon from '@/assets/images/reasonIcon.svg?react'; // eslint-disable-line import/no-unresolved
import InterestPaymentOnly from '@/assets/images/interestPaymentOnly.svg?react'; // eslint-disable-line import/no-unresolved
import PaymentOfPrincipalAndInterest from '@/assets/images/paymentOfPrincipalAndInterest.svg?react'; // eslint-disable-line import/no-unresolved
import PaymentAtTheEndOfThePeriod from '@/assets/images/paymentAtTheEndOfThePeriod.svg?react'; // eslint-disable-line import/no-unresolved

const ReverseMortgageRouteType = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [apiCg] = useSaveMutation();
  const [calculateMaxLoan] = useReverseMortgageCalculateMaxLoanMutation();
  const navigate = useNavigate();
  const { extraDetails, id } = useSelector((state) => state.proposal);
  const {
    reverseMortgageAction,
    propertyValuation,
    youngestBorrowerDOB,
    oldestBorrowerDOB,
  } = extraDetails;
  useIdInStoreGuard();

  const required = t('globals.validations.required');

  const formValidations = {
    reverseMortgageAction: (value) => validate(value, { required }),
  };

  const initialValues = {
    reverseMortgageAction,
  };

  const form = useForm({
    mode: 'controlled',
    validateInputOnBlur: true,
    initialValues,
    validate: formValidations,
    validateInputOnSubmit: true,
  });

  const { setFieldValue } = form;

  const reverseMortgageActionsOptionsArray = [
    {
      value: REVERSE_MORTGAGE_ACTIONS.interestPaymentOnly,
      icon: <InterestPaymentOnly />,
      label: t('pages.ReverseMortgageRouteType.options.interestPaymentOnly'),
      description: t('pages.ReverseMortgageRouteType.options.interestPaymentOnlyDescription'),
    },
    {
      value: REVERSE_MORTGAGE_ACTIONS.paymentOfPrincipalAndInterest,
      icon: <PaymentOfPrincipalAndInterest />,
      label: t('pages.ReverseMortgageRouteType.options.paymentOfPrincipalAndInterest'),
      description: t('pages.ReverseMortgageRouteType.options.interestPaymentOnlyDescription'),
    },
    {
      value: REVERSE_MORTGAGE_ACTIONS.paymentAtTheEndOfThePeriod,
      icon: <PaymentAtTheEndOfThePeriod />,
      label: t('pages.ReverseMortgageRouteType.options.paymentAtTheEndOfThePeriod'),
      description: t('pages.ReverseMortgageRouteType.options.interestPaymentOnlyDescription'),
    },
  ];

  const handleSubmit = async (value) => {
    setLoading(true);

    const { reverseMortgageAction: selectedReverseMortgageAction } = value;

    const objectToSend = {
      reverseMortgageAction: selectedReverseMortgageAction,
      id,
    };

    const { isError } = await getMutationResult(objectToSend, apiCg);

    if (isError) {
      setLoading(false);

      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
      return;
    }

    // Calculate maximum loan amount after successful save
    try {
      const borrowerDOB = youngestBorrowerDOB || oldestBorrowerDOB;

      if (propertyValuation && borrowerDOB) {
        const { data } = await getMutationResult({
          propertyValuation,
          youngestBorrower: toISOString(borrowerDOB),
          reverseMortgageAction: selectedReverseMortgageAction,
        }, calculateMaxLoan);

        if (data?.data?.highestAmount) {
          dispatch(updateExtraDetails({
            reverseMortgageAction: selectedReverseMortgageAction,
            highestLoan: data.data.highestAmount,
          }));
          navigate(routeWithParams(getRouteById(routes.newProposal.id, 'loanAmountReverseMortgage').path, { id }));
        } else {
          notifications.show({
            title: t('globals.error.title'),
            message: t('globals.error.message'),
          });
        }
      } else {
        // dispatch(updateExtraDetails({ reverseMortgageAction: selectedReverseMortgageAction }));
      }
    } catch (error) {
      console.error('Error calculating max loan amount:', error);
      // dispatch(updateExtraDetails({ reverseMortgageAction: selectedReverseMortgageAction }));
    }
  };

  const handleReturn = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, 'bankingConduct').path, { id }));
  };

  return (
    <ProcessHeader
      headerText="סוג מסלול"
      headerColor="#F6EBFF"
      headerTextColor="#9F1FF4"
      subtitle="בחרו את סוג המסלול המתאים לצרכים שלכם"
      showReturnButton
      onReturn={handleReturn}
      icon={ReasonIcon}
    >
      <form onSubmit={form.onSubmit((v) => handleSubmit(v))}>
        <Page.Section >
          <Radiobutton
            options={reverseMortgageActionsOptionsArray}
            value={form.values.reverseMortgageAction}
            form={form}
            fieldName="reverseMortgageAction"
            onChange={(value) => {
              const loan = reverseMortgageActionsOptionsArray.filter(
                (loanTypeObject) => loanTypeObject.value === value,
              );
              setFieldValue('reverseMortgageAction', loan[0].value);
            }}
          />
        </Page.Section>

        <Page.Actions>
          <ProcessButton type="submit" loading={loading} />
        </Page.Actions>
      </form>
    </ProcessHeader>
  );
};

export default ReverseMortgageRouteType;
